/**
 * The one lifetime shared by everything a paused auth flow leaves behind.
 *
 * On this clock, seven per-tab copies and five mirrors:
 *
 *  - the first two per-tab copies -- the auth intent (`meetball_auth_intent`,
 *    @utils/authIntent) and the redirect path (`meetball_redirect_path`,
 *    @utils/auth) -- written at the door and consumed at `/callback`;
 *  - the THIRD per-tab copy, the `/auth/link` params stash
 *    (`meetball_auth_link_params`, @lib/magicLink/authLinkParams), written at
 *    module evaluation when a link arrives and spent by the redemption. It is
 *    the only entry on this clock that holds a live CREDENTIAL rather than a
 *    note about one -- the one-time token itself -- so it is the entry where the
 *    boundary being right matters most, and it is expired through this constant
 *    at its slot in that module. It is deliberately not a mirror: a
 *    one-time token must not outlive the tab that received it;
 *  - the FOURTH per-tab copy, the "Signed in as" cue (`meetball_signed_in_cue`,
 *    @lib/magicLink/signedInCue), armed by a redemption nobody clicked for and
 *    consumed at `/callback`. It was a mirror until #1117; it is per-tab because the
 *    redemption it describes never leaves the tab that armed it;
 *  - the FIFTH to SEVENTH per-tab copies: the redemption address
 *    (`meetball_auth_link_email`, @lib/magicLink/redemptionEmail) and the two
 *    cross-tab flags (`meetball_signed_out_by_other_tab` and
 *    `meetball_user_initiated_signin`, @utils/crossTabAuthSync/authFlowFlags);
 *  - ALL FIVE localStorage mirrors (@utils/magicLinkMirror), because every mirror
 *    is read back through the one TTL'd envelope there. Ordinals are the declaration
 *    order in `MAGIC_LINK_MIRROR_KEYS`, which is what `magicLinkMirror.ts` counts by.
 *    `redirectPath`, `intent`, `pendingQuestionClaim` and `firstSendAddress` (first
 *    to fourth) are armed at the door and consumed at `/auth/link`.
 *    `firstSendAddress` is the first-recipient binding, and it is on this clock for
 *    the sharpest version of the reason: it guards the capabilities in the first two,
 *    so outliving them would be pointless and being outlived by them is the hole it
 *    exists to close. The FIFTH,
 *    `continuation`, holds the pending `{flowId, requestSecret}` pairs the door polls
 *    on, and it is on this clock twice over: through the shared envelope like every
 *    other mirror, and through the per-record `createdAt` pruning in
 *    `@lib/magicLink/continuation`, which is the authoritative one because it is also
 *    what the NATIVE copy (Capacitor Preferences, no envelope) runs on. Same number,
 *    because the pair dies with the magic-link token it belongs to.
 *
 * The first two per-tab copies and their mirrors are written in the same breath at the
 * door, so they have to expire together or the pair disagrees about what this flow
 * carries. The third has no mirror and is written at the other end of the same flow;
 * it is on this clock because it expires the token the mirrors are notes ABOUT, so a
 * stash outliving them would be a credential outliving its own flow.
 *
 * Every reader on this constant must also agree on the BOUNDARY, not just the
 * number: `>= AUTH_FLOW_TTL_MS` is expired. Counted at the comparison, so a
 * maintainer editing one reader has a signal for the others. There are FOUR
 * comparisons:
 *
 *  - `decodeEnvelope` in @utils/sessionSlot (`>=`). Every `{value, writtenAt}`
 *    envelope reads through it: all five mirrors (@utils/magicLinkMirror), the
 *    `/auth/link` stash, the cue, the redemption address and the two cross-tab
 *    flags;
 *  - @lib/magicLink/continuation, which spells the LIVE side,
 *    `Math.abs(now - createdAt) < AUTH_FLOW_TTL_MS` -- the same instant from the
 *    other direction, and two-sided against a clock correction;
 *  - @utils/authIntent (`>= TTL_MS`, aliased to this constant at its declaration);
 *  - @utils/auth (`< SESSION_REDIRECT_TTL_MS`, aliased the same way).
 *
 * The two aliases are why they are easy to miss: grepping the constant's name
 * finds the import and not the comparison.
 *
 * Two stashes in the same flow are deliberately NOT on it. The native durable copy
 * of the intent (Capacitor Preferences) survives an app kill and never takes the
 * magic-link path, so it runs on the shorter `NATIVE_INTENT_TTL_MS` in
 * @utils/authIntent; the post-sign-in auto-join hand-off shadows an intent that has
 * already been consumed and runs on its own shorter window (`INTENT_TTL_MS`,
 * @lib/overlay/intents).
 * The per-tab pending-question flag (`intended_action`) has no clock of its own at
 * all -- only its mirror does.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: 30 min = the backend's MAGIC_LINK_EXPIRES_IN_SECONDS (1800).
 * The per-tab copies used to retire at 10 while the mirrors already ran on 30, and the gap that
 * asymmetry left open was the DOOR-SIDE READ, not the redemption. `mirrorAuthIntentForMagicLink`
 * and `mirrorRedirectPathForMagicLink` run on every arrival at `/auth` and rewrite the mirrors
 * from THIS TAB's TTL'd sessionStorage -- and they CLEAR on an absent value. So a second pass
 * through the door more than 10 minutes after the guest tapped Going (Callback's "Sign in again"
 * retry, or a reload of the door to request a fresh link) read an already-retired stash,
 * re-mirrored nothing, and sent a link that could only land on /home with the RSVP dropped.
 *
 * It was NOT the redemption that was broken, which is what this note used to claim: the mirror was
 * already on 30 minutes, and both `/auth/link` hydrators re-stamp the per-tab clock as they
 * hydrate (`setAuthIntent(mirrored)`, and a fresh `meetball_redirect_timestamp`), so a link opened
 * at minute 12 always arrived to a freshly stamped stash. That re-stamping is also why this TTL
 * bounds one LEG of a flow rather than the flow: door -> link -> failed callback -> door renews it.
 * What bounds the whole thing is the explicit clears -- consumption in `useCallbackRedirect`,
 * `/callback`'s /home exits, and sign-out's sweep.
 *
 * AIDEV-NOTE [MEETBALL magic-link]: 10 -> 30 widens MORE than the magic-link leg, and that cost is
 * accepted rather than unnoticed: `hasAuthIntent` / `readAuthIntent` gate EVERY sign-in in the app
 * (useCallbackRedirect.ts:140, useSignInOut.ts:464, useAuthNavigation.ts:141, SignupFlow.tsx:78,
 * submitHelpers.ts:208, and useNativePostAuth.ts:112 on native), so an abandoned intent now stays
 * live for half an hour against an unrelated Continue-with-Google where it used to die at 10
 * minutes. It is bounded to the SAME TAB (sessionStorage is scoped to the one tab that wrote it),
 * so the exposure is one visitor resuming their own stale action, not somebody else's; the
 * profile-wide copies are the mirrors, and those are rewritten at the door and voided by
 * `/callback` on any error.
 */
export const AUTH_FLOW_TTL_MS = 30 * 60 * 1000;
